import type { FC, FormEvent, ReactNode } from 'react'
import type { ContactPayload } from '@/types'

import { useState } from 'react'

import { cn } from '@/lib/utils'
import { Reveal } from '@/components/generals/reveal'
import { WordReveal } from '@/components/generals/word-reveal'
import { InlineArea, InlineField } from '@/components/contact/inline-field'
import { useMagnetic } from '@/hooks/use-magnetic'

import { EASE_CLASS, MICRO_FAINT } from '@/constants/type'
import { siteConfig } from '@/constants/site'

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/

const EMPTY: ContactPayload = {
    name: '',
    company: '',
    email: '',
    subject: '',
    message: ''
}

type Status = 'idle' | 'sending' | 'sent' | 'error'

const countWords = (value: string): number => {
    return value.trim() ? value.trim().split(/\s+/).length : 0
}

export const LetterForm: FC = (): ReactNode => {
    const [form, setForm] = useState<ContactPayload>(EMPTY)
    const [status, setStatus] = useState<Status>('idle')
    const [invalid, setInvalid] = useState<string[]>([])
    const [note, setNote] = useState('')

    const send = useMagnetic<HTMLSpanElement>(0.18)

    const set = (key: keyof ContactPayload) => (value: string) => {
        setForm(current => ({ ...current, [key]: value }))
        setInvalid(current => current.filter(field => field !== key))
    }

    const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
        event.preventDefault()

        if (status === 'sending') return

        const missing: string[] = []

        if (!form.name.trim()) missing.push('name')
        if (!EMAIL_PATTERN.test(form.email.trim())) missing.push('email')
        if (!form.message.trim()) missing.push('message')

        setInvalid(missing)

        if (missing.length) {
            setStatus('idle')
            setNote('A name, an address that works, and a line or two about the project.')
            return
        }

        setNote('')
        setStatus('sending')

        try {
            const response = await fetch('/api/contact', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(form)
            })

            const data = await response.json()

            if (!response.ok || !data?.ok) {
                setStatus('error')
                setInvalid(Array.isArray(data?.fields) ? data.fields : [])
                setNote(data?.error ?? 'That did not go through. Please try again.')
                return
            }

            setStatus('sent')
        } catch {
            setStatus('error')
            setNote('That did not go through. Please try again in a moment.')
        }
    }

    if (status === 'sent') {
        return (
            <div>
                <p className={MICRO_FAINT}>Filed</p>

                <WordReveal
                    as='p'
                    text='Thank you. It is on its way.'
                    delay={0.1}
                    className='mt-8 max-w-[16ch] font-serif tracking-[-0.02em] text-fz-ink'
                    style={{ fontSize: 'clamp(2rem, 5vw, 3.5rem)', lineHeight: 1.06 }}
                />

                <Reveal delay={0.35}>
                    <p className='mt-8 max-w-[46ch] text-[15px] leading-relaxed text-fz-sub'>
                        We read everything ourselves, so a reply takes a day or two rather than an
                        hour. If it is urgent, the telephone is the faster route.
                    </p>

                    <button
                        type='button'
                        onClick={() => {
                            setForm(EMPTY)
                            setStatus('idle')
                            setNote('')
                            setInvalid([])
                        }}
                        className={`group mt-10 inline-flex items-center gap-5 transition-colors duration-300 hover:text-fz-ink ${MICRO_FAINT}`}
                    >
                        Write another
                        <span
                            aria-hidden='true'
                            className={`block h-px w-10 bg-fz-rule transition-all duration-700 group-hover:w-16 group-hover:bg-fz-rule-on ${EASE_CLASS}`}
                        />
                    </button>
                </Reveal>
            </div>
        )
    }

    const words = countWords(form.message)

    return (
        <form onSubmit={onSubmit} noValidate>
            <Reveal>
                <p className={MICRO_FAINT}>The letter</p>
            </Reveal>

            <WordReveal
                as='p'
                trigger='scroll'
                text={`Hello ${siteConfig.name},`}
                className='mt-8 font-serif tracking-[-0.02em] text-fz-ink'
                style={{ fontSize: 'clamp(1.625rem, 3.2vw, 2.5rem)', lineHeight: 1 }}
            />

            <div
                className='mt-10 max-w-[46ch] text-fz-sub'
                style={{ fontSize: 'clamp(1.0625rem, 1.5vw, 1.25rem)', lineHeight: 2.1 }}
            >
                <Reveal delay={0.15}>
                    <p>
                        my name is{' '}
                        <InlineField
                            name='name'
                            label='Your name'
                            placeholder='your name'
                            value={form.name}
                            onChange={set('name')}
                            autoComplete='name'
                            invalid={invalid.includes('name')}
                            minChars={9}
                        />{' '}
                        and I work at{' '}
                        <InlineField
                            name='company'
                            label='Company or studio (optional)'
                            placeholder='somewhere'
                            value={form.company}
                            onChange={set('company')}
                            autoComplete='organization'
                            minChars={9}
                        />
                        . You can write back to{' '}
                        <InlineField
                            name='email'
                            label='Your email address'
                            placeholder='you@studio.com'
                            value={form.email}
                            onChange={set('email')}
                            type='email'
                            autoComplete='email'
                            invalid={invalid.includes('email')}
                            minChars={13}
                        />
                        .
                    </p>
                </Reveal>

                <Reveal delay={0.26}>
                    <p className='mt-6'>
                        I would like to talk about{' '}
                        <InlineField
                            name='subject'
                            label='Subject (optional)'
                            placeholder='a few words'
                            value={form.subject}
                            onChange={set('subject')}
                            minChars={11}
                        />
                        .
                    </p>
                </Reveal>

                <Reveal delay={0.37}>
                    <p className='mt-6'>Here is the shape of it:</p>

                    <div className='mt-3'>
                        <InlineArea
                            name='message'
                            label='Your message'
                            placeholder='what it is, who it is for, and roughly when'
                            value={form.message}
                            onChange={set('message')}
                            invalid={invalid.includes('message')}
                        />
                    </div>

                    <div className='mt-3 flex justify-end'>
                        <span
                            aria-hidden='true'
                            className={`tabular-nums transition-opacity duration-500 ${MICRO_FAINT}`}
                            style={{ opacity: words ? 1 : 0 }}
                        >
                            {words} {words === 1 ? 'word' : 'words'}
                        </span>
                    </div>
                </Reveal>
            </div>

            <Reveal delay={0.48}>
                <div className='mt-10 flex flex-col gap-6'>
                    <button
                        type='submit'
                        disabled={status === 'sending'}
                        onPointerMove={send.onPointerMove}
                        onPointerLeave={send.onPointerLeave}
                        className='group inline-flex items-center gap-6 self-start disabled:cursor-progress'
                    >
                        <span
                            ref={send.ref}
                            className={`inline-block font-serif tracking-[-0.02em] text-fz-ink transition-[transform,opacity] duration-300 group-hover:opacity-60 ${EASE_CLASS}`}
                            style={{ fontSize: 'clamp(1.75rem, 3.6vw, 2.75rem)', lineHeight: 1 }}
                        >
                            {status === 'sending' ? 'Sending' : 'Send it'}
                        </span>

                        <span
                            aria-hidden='true'
                            className={cn(
                                'block h-px bg-fz-rule-on',
                                EASE_CLASS,
                                status === 'sending'
                                    ? 'fz-sweep w-24'
                                    : 'w-14 transition-all duration-700 group-hover:w-24'
                            )}
                        />
                    </button>

                    <p
                        role='status'
                        aria-live='polite'
                        className='min-h-[1.2em] max-w-[46ch] text-[13px] leading-relaxed transition-colors duration-300'
                        style={{ color: note ? 'var(--fz-alert)' : 'var(--fz-faint)' }}
                    >
                        {note}
                    </p>
                </div>
            </Reveal>
        </form>
    )
}
